import React from 'react';
import { useTranslation } from 'react-i18next';

import Popover from 'components/Popover';
import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';

import { FILTRATION_ICONS, INTL_MAPPING } from '../../../utils/constants';
import { FILTER_OPERATIONS, ISelectOperationProps } from '../../../utils/types';

import {
  IconContainer,
  Operation,
  OperationInfo,
  OperationName,
  OperationsList,
} from './styled';

interface IFilterTypeButton {
  children?: React.ReactNode;
  columnKey: string;
  isDisabled?: boolean;
  onOpenPopover?: () => void;
  onSelectOperation?: (data: ISelectOperationProps) => void;
  operations: FILTER_OPERATIONS[];
  selectedOperation?: FILTER_OPERATIONS | null;
  showSelected?: boolean;
}

const FilterOperationButton = ({
  children,
  columnKey,
  isDisabled,
  onOpenPopover,
  onSelectOperation,
  operations,
  selectedOperation,
  showSelected = true,
}: IFilterTypeButton) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);

  const onOpen = React.useCallback(() => {
    setIsOpen(true);
    onOpenPopover?.();
  }, [onOpenPopover]);

  const onClose = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const renderPopoverContent = () => {
    return (
      <OperationsList data-testid='operationsList'>
        {operations.map((operation, index) => {
          const selectOperation = () => {
            onClose();
            onSelectOperation?.({ columnKey, operation });
          };

          const currentSelected = selectedOperation
            ? selectedOperation === operation
            : !index;
          const isSelected = showSelected ? currentSelected : false;

          return (
            <Operation
              $isSelected={isSelected}
              data-testid={`operation-${operation}`}
              key={operation}
              onClick={selectOperation}
            >
              <OperationInfo>
                <IconContainer>{FILTRATION_ICONS[operation]}</IconContainer>
                <OperationName size='sm' variant='regular'>
                  {t(INTL_MAPPING[operation] as never)}
                </OperationName>
              </OperationInfo>
              {isSelected && <ICONS_MAP.Check width={12} />}
            </Operation>
          );
        })}
      </OperationsList>
    );
  };

  return (
    <Popover
      component={renderPopoverContent}
      disabled={isDisabled}
      onClose={onClose}
      onOpen={onOpen}
      open={isOpen}
      placement='bottom-start'
      positionGap={4}
    >
      {children}
    </Popover>
  );
};

export default appReactMemo(FilterOperationButton);
